import { signal } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationListApiService } from '../services/notification-list-api.service';

export interface NotificationItem {
    id: string;
    templateName: string;
    templateContent: string;
    createdOn: string;
    isRead: boolean;
    additionalInfo: any;
    userId?: string;
}

export class NotificationListViewModel {
    notifications = signal<NotificationItem[]>([]);
    filteredNotifications = signal<NotificationItem[]>([]);
    searchText = signal<string>('');
    isLoading = signal<boolean>(false);

    constructor(
        private notificationListApiService: NotificationListApiService,
        private router: Router
    ) {}

    fetchNotifications(): void {
        this.isLoading.set(true);
        this.notificationListApiService.getNotificationList().subscribe({
            next: (response: any[]) => {
                const parsed = response.map(item => this.parseNotification(item));
                this.notifications.set(parsed);
                this.filteredNotifications.set([...parsed]);
                this.isLoading.set(false);
            },
            error: (err) => {
                console.error('Error fetching notifications:', err);
                this.isLoading.set(false);
            }
        });
    }

    applyFilter(): void {
        const searchText = this.searchText();
        if (!searchText.trim()) {
            this.filteredNotifications.set([...this.notifications()]);
            return;
        }

        const searchLower = searchText.toLowerCase();
        this.filteredNotifications.set(
            this.notifications().filter(notification =>
                notification.templateName.toLowerCase().includes(searchLower) ||
                notification.templateContent.toLowerCase().includes(searchLower)
            )
        );
    }

    readMore(notification: NotificationItem): void {
        this.notificationListApiService.markAsRead(notification.id).subscribe({
            next: () => {
                notification.isRead = true;

                const additionalInfo = notification.additionalInfo || {};
                const executionRunId = additionalInfo.executionRunId || '';
                const scenarioIds = additionalInfo.scenarioIds
                    ? (Array.isArray(additionalInfo.scenarioIds)
                        ? additionalInfo.scenarioIds.join(',')
                        : additionalInfo.scenarioIds)
                    : '';

                const queryParams: any = {};
                if (executionRunId) {
                    queryParams.executionRunId = executionRunId;
                }
                if (scenarioIds) {
                    queryParams.scenarioIds = scenarioIds;
                }
                if (additionalInfo.clientId) {
                    queryParams.clientId = additionalInfo.clientId;
                }
                if (additionalInfo.packageId) {
                    queryParams.packageId = additionalInfo.packageId;
                }
                if (additionalInfo.functionalAreaId) {
                    queryParams.functionalAreaId = additionalInfo.functionalAreaId;
                }
                if (additionalInfo.testTypeId) {
                    queryParams.testTypeId = additionalInfo.testTypeId;
                }
                if (additionalInfo.tenantId) {
                    queryParams.tenantId = additionalInfo.tenantId;
                }

                this.router.navigate(['/execution-results'], { queryParams }).catch(err => {
                    console.error('Navigation error:', err);
                });
            },
            error: (err) => {
                console.error('Error marking notification as read:', err);
            }
        });
    }

    private parseNotification(item: any): NotificationItem {
        try {
            const additionalInfo = typeof item.additionalInfo === 'string'
                ? JSON.parse(item.additionalInfo || '{}')
                : (item.additionalInfo || {});

            return {
                id: item.id,
                templateName: additionalInfo?.templateName || 'Execution Completed',
                templateContent: this.buildContentSummary(additionalInfo),
                createdOn: item.createdOn,
                isRead: item.isRead || false,
                additionalInfo: additionalInfo,
                userId: item.userId
            };
        } catch (e) {
            return {
                id: item.id,
                templateName: 'Execution Completed',
                templateContent: 'Execution completed',
                createdOn: item.createdOn,
                isRead: item.isRead || false,
                additionalInfo: {},
                userId: item.userId
            };
        }
    }

    private buildContentSummary(info: any): string {
        const count = info.scenarioIds?.length || 0;
        return count > 0 ? `Execution completed for ${count} scenarios` : 'Execution completed';
    }
}
